import React, { useContext } from 'react';
import { string, shape, arrayOf, bool, number } from 'prop-types';
import classNames from 'classnames';
import StyledLabel from '../styled-label';
import componentEnhance from '../../lib/component-enhance';
import StaticPropsContext from '../context/static-props';
import { trackEvent } from '../../lib/tracking';

const NAMESPACE = 'ui-vpp-publish-vehicle-cta';

const SITES_WITH_QUOTER_DISCLAIMER = ['MLB', 'MLM'];
const DEFAULT_SUBTITLES = [];

const PublishVehicleCta = ({ title = null, subtitles = DEFAULT_SUBTITLES, action = null, legal_disclaimer = null }) => {
  const {
    target,
    track,
    label: { text },
  } = action;
  const context = useContext(StaticPropsContext);
  const { deviceType, siteId } = context || {};

  const shouldShowQuoterDisclaimer = !!legal_disclaimer && siteId && SITES_WITH_QUOTER_DISCLAIMER.includes(siteId);

  const handleInteraction = e => {
    e.preventDefault();
    trackEvent(track);
    window.location.href = target;
  };

  const handleOnClick = e => {
    handleInteraction(e);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleInteraction(e);
    }
  };

  return (
    <div
      className={classNames(`${NAMESPACE}`, deviceType, { [`${NAMESPACE}--no-legal`]: !shouldShowQuoterDisclaimer })}
      id="publish-vehicle-cta"
    >
      {title && <h2 className={`${NAMESPACE}__feature__title`}>{title.text}</h2>}

      {subtitles && subtitles.length > 0 && (
        <ul className={`${NAMESPACE}__subtitles`}>
          {subtitles.map(subtitle => (
            <li key={subtitle.label.text} className={`${NAMESPACE}__subtitles__subtitle`}>
              <StyledLabel
                as="p"
                color={subtitle.label.color}
                font_size={subtitle.label.font_size}
                font_family={subtitle.label.font_family}
                text={componentEnhance.jsx(subtitle.label.text, subtitle.label.values)}
              />
            </li>
          ))}
        </ul>
      )}

      {action && (
        <div className={`${NAMESPACE}__container`}>
          <button
            type="button"
            className={`${NAMESPACE}__publish-vehicle-button ${NAMESPACE}__publish-vehicle-button--quiet`}
            onClick={handleOnClick}
            onKeyDown={handleKeyDown}
            aria-disabled="false"
            aria-label={text}
          >
            <span>{text}</span>
          </button>
        </div>
      )}

      {shouldShowQuoterDisclaimer && (
        <span className={`${NAMESPACE}__legal_disclaimer`}>
          <StyledLabel
            as="p"
            color={legal_disclaimer.color}
            font_size={legal_disclaimer.font_size}
            font_family={legal_disclaimer.font_family}
            text={componentEnhance.jsx(legal_disclaimer.text, legal_disclaimer.values)}
          />
        </span>
      )}
    </div>
  );
};

PublishVehicleCta.propTypes = {
  title: shape({
    text: string,
    color: string,
    font_size: string,
    font_family: string,
  }),
  subtitles: arrayOf(
    shape({
      char_icon: shape({
        text: string,
        color: string,
        font_size: string,
        font_family: string,
      }),
      label: shape({
        text: string,
        color: string,
        font_size: string,
        font_family: string,
      }),
    }),
  ),
  action: shape({
    type: string,
    target: string,
    html_target: string,
    timeout: number,
    duration: number,
    label: shape({
      text: string.isRequired,
      font_size: string,
      font_family: string,
    }).isRequired,
    track: shape({
      melidata_event: shape({
        path: string,
        event_data: shape({
          item_id: string,
          seller_id: number,
          listing_type_id: string,
          category_id: string,
          vertical: string,
          item_status: string,
        }),
      }),
    }),
    disabled: bool,
    action_type: string,
  }),
  legal_disclaimer: shape({
    text: string.isRequired,
    color: string,
    font_size: string,
    font_family: string,
    values: shape({
      blue_text: shape({
        type: string,
        target: string,
        html_target: string,
        timeout: number,
        duration: number,
        label: shape({
          text: string,
          color: string,
          font_size: string,
          font_family: string,
        }),
      }),
    }),
  }),
};

export default PublishVehicleCta;
